import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Modal, Spin } from 'antd'
import { DefaultJsonForm } from '../../../../../components/v2/default-json-form'
import schema from './schema.json'
import { convertData2JsonForm } from '../../../../../shared/lib/convertData2JsonForm'
import fetchAPI from '../../../../../../lib/utils/fetch-api'
import { isFormDataRequired } from '../../../../../../lib/utils/collections'
import dayjs from 'dayjs'
import { IFormData, ISchemaActual, MeasurementModalProps, TimeResponse } from './interfaces'
import { setMeasurementEditDate } from '../../api/setMeasurementEditDate'
import { MeasurementFormDataI } from '../../types/types'

export const MeasurementEditDateModal: React.FC<MeasurementModalProps> = memo(
  (props: MeasurementModalProps) => {
    const { id, isModalOpen, onOk, onCancel } = props

    const [error, setError] = useState<string | null>(null)
    const [loadingTimeOptions, setLoadingTimeOptions] = useState(false)
    const [formData, setFormData] = useState<IFormData>({
      date: undefined,
      time: [],
      comment: undefined,
    })
    const [disabledDates, setDisabledDates] = useState<string[]>([])
    const [availableTimes, setAvailableTimes] = useState<any>(null)
    const [defaultTimes, setDefaultTimes] = useState<any>(null)
    const [responsible, setResponsible] = useState<null | number>(null)

    const fetchAvailableTimes = useCallback(async () => {
      setLoadingTimeOptions(true)
      try {
        const response = (await fetchAPI(`/api/measurement-time/${id}`, {
          method: 'POST',
        })) as TimeResponse
        setAvailableTimes(response?.timeOptions || [])
        setDefaultTimes(response?.defaultTimes || [])
        setDisabledDates(response.disabledDates)
      } catch (error) {
        setError('Не удалось загрузить доступное время')
        console.error('Ошибка при загрузке времени:', error)
      } finally {
        setLoadingTimeOptions(false)
      }
    }, [formData.date])

    useEffect(() => {
      if (isModalOpen) {
        fetchAvailableTimes()
      }
    }, [isModalOpen])

    // Сброс measurement_time при изменении measurement_date
    useEffect(() => {
      if (!formData.date) {
        setFormData(prev => ({ ...prev, time: undefined }))
      }
    }, [formData.date])

    const handleOk = useCallback(async () => {
      const isRequiredFields = isFormDataRequired(formData, actualSchema)
      if (!isRequiredFields) {
        Modal.warning({
          title: 'Предупреждение',
          content: 'Заполните все обязательные поля',
        })
        return
      }

      const controller = new AbortController()
      const data: MeasurementFormDataI = {
        id: id,
        date: formData?.date || undefined,
        time: formData?.time || [],
        comment: formData.comment,
      }
      await setMeasurementEditDate(data, controller.signal)
        .then(response => {
          if (response?.error) {
            Modal.error({
              title: 'Ошибка',
              content: `При сохранении скорректированной даты возникла ошибка: ${response?.error}.`,
            })
            return
          }
          onOk?.(response)
        })
        .catch(() =>
          Modal.error({
            title: 'Ошибка',
            content: 'При сохранении скорректированной даты возникла ошибка. Дата не изменена.',
          }),
        )
    }, [formData, onOk])

    const handleCancel = (e: React.MouseEvent<HTMLElement>) => {
      setFormData({
        date: undefined,
        time: [],
        comment: undefined,
      })
      setError(null)
      setAvailableTimes(null)
      setDefaultTimes(null)
      setDisabledDates([])
      setLoadingTimeOptions(false)
      setResponsible(null)
      onCancel?.(e)
    }

    const onChange = useCallback(
      data => {
        if (!data.formData.date) {
          setFormData(prevData => ({
            ...prevData,
            ...data.formData,
            time: [],
          }))
        } else {
          setFormData(prevData => ({
            ...prevData,
            ...data.formData,
          }))
        }
      },
      [setFormData, responsible, setResponsible],
    )

    const actualSchema = useMemo(() => {
      const schemaActual = convertData2JsonForm(schema.fields) as never as ISchemaActual
      const { date } = formData
      // Обновляем опции для доступного времени
      if (date) {
        // Находим timeOptions для выбранной даты
        const dateStr = dayjs(date).format('YYYY-MM-DD').toString()
        const timeOptions = availableTimes.find(i => i.date === dateStr)?.times || defaultTimes
        schemaActual.resultSchema.properties.time.oneOf = timeOptions.map(option => ({
          const: option.value,
          title: option.value,
          disabled: option.disabled,
        }))
        schemaActual.resultSchema.properties.time.enum = timeOptions.map(item => item.value)
      }
      schemaActual.resultSchema.properties.time.options = {
        ...schemaActual.resultSchema.properties.time.options,
        placeholder: loadingTimeOptions ? 'Загрузка' : 'Время замера',
        disabled: !Boolean(date) || loadingTimeOptions,
      }

      return schemaActual.resultSchema || {}
    }, [schema, formData, disabledDates, defaultTimes, loadingTimeOptions])

    if (!isModalOpen) {
      return null
    }

    return (
      <Modal
        title="Изменение даты"
        onOk={handleOk}
        onCancel={handleCancel}
        open={isModalOpen}
        destroyOnHidden={true}
        okButtonProps={{ disabled: loadingTimeOptions }}
      >
        {loadingTimeOptions ? (
          <Spin />
        ) : error ? (
          <Alert message={error} type="error" showIcon />
        ) : (
          <div>
            <DefaultJsonForm
              formData={formData}
              schema={actualSchema}
              onChange={onChange}
              orientation="horizontal"
            />
          </div>
        )}
      </Modal>
    )
  },
)

MeasurementEditDateModal.displayName = 'MeasurementEditDateModal'
